import { useCallback, useEffect, useRef, useState } from "react";

import { styled } from "@linaria/react";
import rrwebPlayer from "rrweb-player";

import { withTheme } from "@gx/dls/theme";
import type { PropsWithTheme } from "@gx/dls/theme/types";

import { BrowserbasePlayerControls } from "@/pages/sources/components/edit/tabs/syncs/browserbase/BrowserbaseReplayViewerControls";
import { BrowserbaseStatusChip } from "@/pages/sources/components/edit/tabs/syncs/browserbase/BrowserbaseStatusChip";
import { BrowserbaseReplayEvent } from "@/pages/sources/components/edit/tabs/syncs/browserbase/types";

import "rrweb-player/dist/style.css";

const BrowserbaseReplayViewerWrapper = withTheme(styled.div<PropsWithTheme>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`);

const BrowserbaseReplayViewerContent = withTheme(styled.div<PropsWithTheme>`
  position: relative;
  width: 100%;
  height: calc(100% - 40px);
  overflow: hidden;

  .rr-player {
    float: none;
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .rr-player__frame {
    background: transparent;
  }

  .rr-controller {
    display: none !important;
  }
`);

const BrowserbaseReplayViewerMount = styled.div`
  width: 100%;
`;

interface BrowserbaseReplayPlayerProps {
  events: BrowserbaseReplayEvent[];
}

interface BrowserbaseReplayPlayerState {
  isPlaying: boolean;
  currentTime: number;
  totalTime: number;
  dimensions: { width: number; height: number } | null;
}

const DEFAULT_STATE: BrowserbaseReplayPlayerState = {
  isPlaying: false,
  currentTime: 0,
  totalTime: 0,
  dimensions: null,
};

export function BrowserbaseReplayPlayer({ events }: BrowserbaseReplayPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<rrwebPlayer | null>(null);
  const [state, setState] = useState<BrowserbaseReplayPlayerState>(DEFAULT_STATE);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      setState((prev) => ({ ...prev, dimensions: { width: rect.width, height: rect.height } }));
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || events.length === 0 || !state.dimensions) return;

    mount.innerHTML = "";

    // Find the meta event to get the original viewport dimensions
    const metaEvent = events.find((e) => e.type === 4);
    const originalWidth = (metaEvent?.data as { width?: number })?.width || 1920;
    const originalHeight = (metaEvent?.data as { height?: number })?.height || 1080;
    const aspectRatio = originalHeight / originalWidth;

    // Fill width and calculate height based on aspect ratio
    const playerWidth = state.dimensions.width;
    const playerHeight = Math.round(playerWidth * aspectRatio);

    const player = new rrwebPlayer({
      target: mount,
      props: {
        events,
        autoPlay: false,
        showController: false,
        skipInactive: true,
        width: playerWidth,
        height: playerHeight,
      },
    });

    playerRef.current = player;

    const replayer = player.getReplayer();
    const metadata = replayer.getMetaData();
    setState((prev) => ({ ...prev, totalTime: metadata.totalTime }));

    return () => {
      playerRef.current = null;
      mount.innerHTML = "";
    };
  }, [events, state.dimensions]);

  useEffect(() => {
    if (!playerRef.current) return;

    const interval = setInterval(() => {
      const replayer = playerRef.current?.getReplayer();
      if (replayer) {
        const metadata = replayer.getMetaData();
        const current = replayer.getCurrentTime();
        setState((prev) => ({ ...prev, currentTime: current }));

        if (current >= metadata.totalTime && state.isPlaying) {
          setState((prev) => ({ ...prev, isPlaying: false }));
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [state.isPlaying]);

  const handlePlayPause = useCallback(() => {
    if (!playerRef.current) return;

    if (state.isPlaying) {
      playerRef.current.pause();
      setState((prev) => ({ ...prev, isPlaying: false }));
    } else {
      const replayer = playerRef.current.getReplayer();
      const metadata = replayer.getMetaData();
      const current = replayer.getCurrentTime();

      if (current >= metadata.totalTime) {
        playerRef.current.goto(0);
        setState((prev) => ({ ...prev, currentTime: 0 }));
      }

      playerRef.current.play();
      setState((prev) => ({ ...prev, isPlaying: true }));
    }
  }, [state.isPlaying]);

  const handleSeek = useCallback((time: number) => {
    if (!playerRef.current) return;
    playerRef.current.goto(time);
    setState((prev) => ({ ...prev, currentTime: time }));
  }, []);

  return (
    <BrowserbaseReplayViewerWrapper>
      <BrowserbaseReplayViewerContent ref={containerRef}>
        <BrowserbaseReplayViewerMount ref={mountRef} />
      </BrowserbaseReplayViewerContent>
      <BrowserbaseStatusChip variant="recorded" bottom={52} />
      <BrowserbasePlayerControls
        isPlaying={state.isPlaying}
        currentTime={state.currentTime}
        totalTime={state.totalTime}
        onPlayPause={handlePlayPause}
        onSeek={handleSeek}
      />
    </BrowserbaseReplayViewerWrapper>
  );
}
